import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useEffect, useMemo } from 'react';

/**
 * GitHubRepo_LifecyclePicker
 *
 * A single-select for the component lifecycle that also enforces the Sandbox
 * governance rule reactively — something a plain JSON-Schema conditional cannot
 * do:
 *
 *  - When the org selected on the first step is Sandbox (`principalsbx-emu`),
 *    the value is forced to `experimental` and the control is disabled/locked.
 *  - When the org is switched AWAY from Sandbox (Back → pick a non-sandbox org),
 *    the previously forced `experimental` value is CLEARED so the user makes a
 *    fresh, deliberate choice.
 *
 * Why a custom field is needed: a `then: { lifecycle: { const: experimental } }`
 * conditional injects `experimental` into the form state and RJSF never retracts
 * it once the condition stops matching — so the value stayed stuck as
 * `experimental` after switching to a non-sandbox org. This field owns that
 * transition explicitly.
 *
 * The stored value is the same `string` (`experimental` | `production`) the
 * built-in enum widget produced, so downstream templating is unchanged. The
 * template still keeps a validation-only `enum: [experimental]` Sandbox rule as
 * a server-side safety net.
 */

const SANDBOX_ORG = 'principalsbx-emu';
const EXPERIMENTAL = 'experimental';

/**
 * The org this picker last observed, remembered at MODULE scope on purpose.
 *
 * The scaffolder wizard unmounts a step's fields when you navigate to another
 * step, so a component-local ref (`useRef`) would reset to `undefined` on
 * remount — meaning the "was Sandbox" check could never fire after clicking
 * Back → change org → Next, and the forced `experimental` value would stay.
 * Module scope survives that remount within the wizard session. It is only
 * read together with `formData === EXPERIMENTAL`, so a stale value left over
 * from a previous wizard run cannot wrongly clear a fresh selection.
 */
let lastSeenOrg: string | undefined;

function parseOwner(repoUrl?: string): string {
  if (!repoUrl) return '';
  try {
    const query = repoUrl.includes('?') ? repoUrl.split('?')[1] : repoUrl;
    return new URLSearchParams(query).get('owner') ?? '';
  } catch {
    return '';
  }
}

export const LifecyclePicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    idSchema,
    formContext,
  } = props;

  const options = useMemo<string[]>(() => {
    return (schema?.enum as string[] | undefined) ?? [];
  }, [schema]);

  const optionLabels = useMemo<string[]>(() => {
    return (schema?.enumNames as string[] | undefined) ?? [];
  }, [schema]);

  const repoUrl = (formContext?.formData as { repoUrl?: string } | undefined)
    ?.repoUrl;
  const org = parseOwner(repoUrl);
  const isSandbox = org === SANDBOX_ORG;

  useEffect(() => {
    const wasSandbox = lastSeenOrg === SANDBOX_ORG;

    if (isSandbox) {
      // Sandbox forces experimental and locks the control.
      if (formData !== EXPERIMENTAL) {
        onChange(EXPERIMENTAL);
      }
    } else if (wasSandbox && formData === EXPERIMENTAL) {
      // Just switched away from Sandbox: drop the value Sandbox forced so the
      // user picks deliberately.
      onChange(undefined);
    }

    lastSeenOrg = org;
    // Only react to org transitions; formData is read from the render that the
    // org change triggered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  const hasError = rawErrors !== undefined && rawErrors.length > 0;

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    onChange(value ? value : undefined);
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError}
      disabled={isSandbox}
      fullWidth
    >
      <InputLabel id={`${idSchema?.$id}-label`}>
        {schema?.title ?? 'Lifecycle'}
      </InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        id={idSchema?.$id}
        value={formData ?? ''}
        onChange={handleChange}
      >
        {options.map((option, index) => (
          <MenuItem key={option} value={option}>
            {optionLabels[index] ?? option}
          </MenuItem>
        ))}
      </Select>
      {isSandbox && (
        <FormHelperText>
          Sandbox (principalsbx-emu) repositories are locked to the Experimental
          lifecycle.
        </FormHelperText>
      )}
      {schema?.description && !isSandbox && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
    </FormControl>
  );
};
